import React, { useState, useEffect, useCallback, useContext } from 'react';

import { Button, Input, Popover, Tag, Badge } from 'antd';

import styled from 'styled-components';

import { readableEventType } from '@libs/EventTypes';

import { useEventsSearchContext, useEventsSearchContextDispatch } from '@contexts/EventsSearchContext';
import { ListContext } from '@contexts/ListContext';

import Switch from '@components/Gui/CustomSwitch';
import FlexContainer from '@components/Gui/FlexContainer';
import MiniLoading from '@components/Gui/MiniLoading';
import NwButtonOption from '@components/Gui/NwButtonOption';
import RadioButtonGroup from '@components/Gui/NwForm/RadioButtonGroup';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import debounce from 'lodash/debounce';

const ButtonGroup = Button.Group;

const EventTypeTag = styled(Tag)`
    cursor: pointer;
    padding: 0.2rem 0.5rem;
    font-size: ${(props) => props.theme.FontSizeSm};
    text-transform: uppercase;
    border-radius: 4px;
    user-select: none;
    transition: all 0.2s;

    &.active {
        background-color: ${(props) => props.theme.PrimaryColor};
        color: #fff;
        border-color: ${(props) => props.theme.PrimaryColor};

        &:hover {
            background-color: ${(props) => props.theme.PrimaryColorDark || props.theme.PrimaryColor};
            border-color: ${(props) => props.theme.PrimaryColorDark || props.theme.PrimaryColor};
        }
    }

    &.inactive {
        background-color: ${(props) => props.theme.LightGrayColor || '#f5f5f5'};
        color: ${(props) => props.theme.TextMutedColor || '#8c8c8c'};
        border-color: ${(props) => props.theme.BorderColor || '#d9d9d9'};

        &:hover {
            background-color: ${(props) => props.theme.BorderColor || '#d9d9d9'};
        }
    }
`;

const PopoverContent = styled.div`
    padding: 0.5rem;
    min-width: 22rem;

    .event-type-switch {
        margin-bottom: 0.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid ${(props) => props.theme.BorderInCard || '#f0f0f0'};
    }

    .event-type-filters {
        .filter-label {
            font-size: ${(props) => props.theme.FontSizeSm};
            font-weight: 600;
            text-transform: uppercase;
            margin-bottom: 0.5rem;
            color: ${(props) => props.theme.TextColor};
        }

        .filter-section {
            margin-bottom: 0.75rem;

            &:last-child {
                margin-bottom: 0;
            }
        }

        .subtypes-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem 0.25rem;
            max-width: 24rem;
        }
    }

    .popover-actions {
        margin-top: 0.75rem;
        padding-top: 0.75rem;
        border-top: 1px solid ${(props) => props.theme.BorderInCard || '#f0f0f0'};
        display: flex;
        gap: 0.5rem;
        justify-content: flex-end;
    }
`;

const StyledHeader = styled.div`
    margin: 0;
    padding: 0;
    position: relative;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #f0f0f0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    min-height: ${(props) => !props.drawerMode && '5rem'};
    padding: 0.5rem;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    gap: 1rem;

    @media only screen and (max-width: 960px) {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }

    .header-filters {
        border-right: 1px solid #e8e8e8;
        flex: 1 0 0;
        padding: 0.5rem 1rem;

        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 0.5rem;

        @media only screen and (max-width: 960px) {
            border-right: none;
            min-height: 0;
            padding: 0;
            flex: none;
            align-items: flex-start;
        }

        .header-filters-first-row,
        .header-filters-second-row {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            flex-wrap: wrap;
            gap: 1rem;

            .header-summary {
                border-right: ${(props) => props.theme.DefaultBorder};
                flex: auto 0 0;
                padding: 0 1rem 0 0;

                @media only screen and (max-width: 960px) {
                    border-right: none;
                    min-height: 0;
                    padding: 0;
                }

                .header-summary-text {
                    font-size: 1em;
                    display: flex;
                    align-items: center;

                    em.counter {
                        font-size: 1rem;
                        font-weight: bold;
                        font-style: normal;
                        padding-right: 0.5em;
                    }
                }
                small.hidden-models {
                    display: block;
                }
            }

            @media only screen and (max-width: 640px) {
                flex-direction: column;
                padding: 0;
            }
        }

        .header-filters-second-row {
            gap: 0.5rem;
        }

        .header-filters-third-row {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 0.25rem;

            .active-filter-item {
                font-size: ${(props) => props.theme.FontSizeSm};
                display: flex;
                gap: 0.25rem;
                .filter-label {
                    text-transform: uppercase;
                    font-weight: 600;
                }
                .filter-item {
                    &:not(:last-child)::after {
                        content: ' • ';
                    }
                }
            }
        }
    }

    .header-actions {
        flex: auto 0 0;
        min-width: 12.5rem;
        text-transform: uppercase;
        font-size: ${(props) => props.theme.FontSizeSm};

        @media only screen and (max-width: 960px) {
            min-height: 0;
            padding: 0;
            min-width: 0;
        }
    }

    div.control {
        padding: 0.5em 0;
        span.label {
            font-size: 0.85em;
            text-transform: uppercase;
            padding: 0 0.5em 0 0;

            &.on-right {
                padding: 0 0 0 0.5em;
            }
        }
    }

    .loading-title {
        margin-left: 8px;
        margin-bottom: 0;
    }
`;

const EventsSearchHeaderDrawer = ({ events, eventsGrouped, loading }) => {
    const { sort, showTransactions, nameFilter, eventFilters, groupByProject, modelId, clientId } =
        useEventsSearchContext();
    const dispatch = useEventsSearchContextDispatch();
    const { cachedList } = useContext(ListContext);

    const [popoverOpen, setPopoverOpen] = useState({});
    const [tempFilters, setTempFilters] = useState({});

    let drawerType = 'global';
    if (modelId && clientId) {
        drawerType = 'client-model';
    } else if (clientId) {
        drawerType = 'client';
    } else if (modelId) {
        drawerType = 'model';
    }

    // If sort is "type" and groupByProject is active, change to "time-down"
    useEffect(() => {
        if (groupByProject && sort === 'type') {
            dispatch({ type: 'SetSort', sort: 'time-down' });
        }
    }, [groupByProject, sort, dispatch]);

    const handleChangeSort = (newSort) => () => {
        dispatch({ type: 'SetSort', sort: newSort });
    };

    const handleChangeNameFilter = (event) => {
        dispatch({ type: 'SetNameFilter', nameFilter: event.target.value });
    };

    const handleGroupEvents = (checked) => {
        dispatch({ type: 'SetGroupByProject', groupByProject: checked });
    };

    const handleShowTransactions = (checked) => {
        dispatch({ type: 'SetShowTransactions', showTransactions: checked });
    };

    const handleToggleEventType = (eventType, enabled) => {
        dispatch({ type: 'ToggleEventType', eventType, enabled });
    };

    const handleUpdateEventTypeConfig = (eventType, config) => {
        dispatch({ type: 'UpdateEventTypeConfig', eventType, config });
    };

    const isEventTypeActive = (eventType) => {
        return eventFilters && Object.hasOwn(eventFilters, eventType);
    };

    const getEventTypeConfig = (eventType) => {
        return eventFilters?.[eventType] || {};
    };

    const handlePopoverChange = (eventType, open) => {
        setPopoverOpen((prev) => ({ ...prev, [eventType]: open }));

        if (open) {
            // Initialize temp filters with current values when opening popover
            const config = getEventTypeConfig(eventType);
            setTempFilters((prev) => ({
                ...prev,
                [eventType]: {
                    eventSubtypeCode: config.eventSubtypeCode || null,
                    optionNumber: config.optionNumber || null,
                    labels: config.labels || null,
                },
            }));
        }
    };

    const hasActiveFilters = (eventType) => {
        const config = getEventTypeConfig(eventType);
        // Check for subtypes
        const hasSubtypes = config.eventSubtypeCode && config.eventSubtypeCode.length > 0;
        // Check for option number (for jobs and ext)
        const hasOptionNumber =
            (eventType === 'job' || eventType === 'ext') &&
            config.optionNumber !== undefined &&
            config.optionNumber !== null;
        // Check for labels (for followup)
        const hasLabels = eventType === 'followup' && config.labels && config.labels.length > 0;
        return hasSubtypes || hasOptionNumber || hasLabels;
    };

    const getActiveFiltersText = (eventType) => {
        const config = getEventTypeConfig(eventType);
        const filters = [];

        // Check for subtypes
        if (config.eventSubtypeCode && config.eventSubtypeCode.length > 0) {
            const typeFilter = eventType === 'casting' ? 'Casting' : eventType === 'ext' ? 'Ext' : 'Job';
            const selectedSubtypes = cachedList.eventSubtypes.items
                .filter((item) => item.Type === typeFilter && config.eventSubtypeCode.includes(item.Code))
                .map((item) => item.Name);

            if (selectedSubtypes.length > 0) {
                filters.push(`subtypes: ${selectedSubtypes.join(', ')}`);
            }
        }
        // Check for option number (for jobs and ext)
        if (
            (eventType === 'job' || eventType === 'ext') &&
            config.optionNumber !== undefined &&
            config.optionNumber !== null
        ) {
            if (config.optionNumber === 10) {
                filters.push('Confirmed only');
            } else {
                filters.push(`Option ${config.optionNumber}`);
            }
        }
        // Check for labels (for followup)
        if (eventType === 'followup' && config.labels && config.labels.length > 0) {
            const selectedLabels = cachedList.followUpLabels.items
                .filter((item) => config.labels.includes(item.Code))
                .map((item) => item.Label);

            if (selectedLabels.length > 0) {
                filters.push(`labels: ${selectedLabels.join(', ')}`);
            }
        }
        return filters;
    };

    const renderActiveFilters = (eventType) => {
        const filters = getActiveFiltersText(eventType);
        if (filters.length === 0) return null;
        return (
            <div className="active-filter-item">
                <span className="filter-label">filters on {readableEventType(eventType, true)}:</span>
                {filters.map((filter, index) => (
                    <span key={index} className="filter-item">
                        {filter}
                    </span>
                ))}
            </div>
        );
    };

    const renderEventTypePopover = (eventType, label) => {
        const isActive = isEventTypeActive(eventType);

        // Use temp filters if available, otherwise use current config
        const tempConfig = tempFilters[eventType] || {};
        const currentConfig = getEventTypeConfig(eventType);
        const config = {
            eventSubtypeCode:
                tempConfig.eventSubtypeCode !== undefined
                    ? tempConfig.eventSubtypeCode
                    : currentConfig.eventSubtypeCode,
            optionNumber: tempConfig.optionNumber !== undefined ? tempConfig.optionNumber : currentConfig.optionNumber,
            labels: tempConfig.labels !== undefined ? tempConfig.labels : currentConfig.labels,
        };

        // Get subtype options for this event type
        const eventSubtypes = cachedList.eventSubtypes.items.filter(
            (item) =>
                item.Type === (eventType === 'casting' ? 'Casting' : eventType === 'ext' ? 'Ext' : 'Job') &&
                !item.Disabled
        );

        // Get followup labels (exclude disabled)
        const followUpLabels = (cachedList.followUpLabels.items || []).filter((item) => !item.Disabled);

        const isSubtypeSelected = (subtypeCode) => {
            const selectedCodes = config.eventSubtypeCode || [];
            return selectedCodes.includes(subtypeCode);
        };

        const isLabelSelected = (code) => {
            const selectedLabels = config.labels || [];
            return selectedLabels.includes(code);
        };

        const isAllSubtypesSelected = !config.eventSubtypeCode || config.eventSubtypeCode.length === 0;
        const isAllLabelsSelected = !config.labels || config.labels.length === 0;

        const handleTempSubTypeToggle = (subtypeCode) => {
            const currentCodes = config.eventSubtypeCode || [];
            let newCodes;

            if (subtypeCode === null) {
                // "All subtypes" clicked
                newCodes = null;
            } else {
                if (currentCodes.includes(subtypeCode)) {
                    // Remove this subtype
                    newCodes = currentCodes.filter((code) => code !== subtypeCode);
                    if (newCodes.length === 0) {
                        newCodes = null;
                    }
                } else {
                    // Add this subtype
                    newCodes = [...currentCodes, subtypeCode];
                }
            }

            setTempFilters((prev) => ({
                ...prev,
                [eventType]: {
                    ...prev[eventType],
                    eventSubtypeCode: newCodes,
                },
            }));
        };

        const handleTempOptionNumberChange = (val) => {
            setTempFilters((prev) => ({
                ...prev,
                [eventType]: {
                    ...prev[eventType],
                    optionNumber: val,
                },
            }));
        };

        const handleTempLabelToggle = (code) => {
            const currentLabels = config.labels || [];
            let newLabels;

            if (code === null) {
                // "All labels" clicked
                newLabels = null;
            } else {
                if (currentLabels.includes(code)) {
                    // Remove this label
                    newLabels = currentLabels.filter((c) => c !== code);
                    if (newLabels.length === 0) {
                        newLabels = null;
                    }
                } else {
                    // Add this label
                    newLabels = [...currentLabels, code];
                }
            }

            setTempFilters((prev) => ({
                ...prev,
                [eventType]: {
                    ...prev[eventType],
                    labels: newLabels,
                },
            }));
        };

        const handleApplyFilters = () => {
            // Apply temp filters to real filters
            handleUpdateEventTypeConfig(eventType, tempConfig);
            handlePopoverChange(eventType, false);
        };

        const handleClearFilters = () => {
            // Reset temp filters
            setTempFilters((prev) => ({
                ...prev,
                [eventType]: {
                    eventSubtypeCode: null,
                    optionNumber: null,
                    labels: null,
                },
            }));
        };

        return (
            <PopoverContent>
                <div className="event-type-switch">
                    <Switch
                        text={`Include ${label}`}
                        checked={isActive}
                        onChange={(checked) => {
                            handleToggleEventType(eventType, checked);
                            if (!checked) {
                                handlePopoverChange(eventType, false);
                            }
                        }}
                    />
                </div>

                {isActive &&
                    (eventType === 'casting' ||
                        eventType === 'job' ||
                        eventType === 'ext' ||
                        eventType === 'followup') && (
                        <>
                            <div className="event-type-filters">
                                {(eventType === 'job' || eventType === 'ext') && (
                                    <div className="filter-section">
                                        <div className="filter-label">Confirmation / Option</div>
                                        <RadioButtonGroup
                                            value={config.optionNumber || null}
                                            onChange={handleTempOptionNumberChange}
                                            size="small"
                                            options={[
                                                { value: null, label: 'all' },
                                                { value: 10, label: 'job' },
                                                ...[...Array(5).keys()].map((key) => ({
                                                    value: key + 1,
                                                    label: key + 1,
                                                })),
                                            ]}
                                        />
                                    </div>
                                )}

                                {(eventType === 'casting' || eventType === 'job' || eventType === 'ext') && (
                                    <div className="filter-section">
                                        <div className="filter-label">
                                            {eventType === 'casting' ? 'Casting' : eventType === 'ext' ? 'Ext' : 'Job'}{' '}
                                            Subtypes
                                        </div>
                                        <div className="subtypes-tags">
                                            <EventTypeTag
                                                className={isAllSubtypesSelected ? 'active' : 'inactive'}
                                                onClick={() => handleTempSubTypeToggle(null)}
                                            >
                                                All subtypes
                                            </EventTypeTag>
                                            {eventSubtypes.map((subtype) => (
                                                <EventTypeTag
                                                    key={subtype.Code}
                                                    className={isSubtypeSelected(subtype.Code) ? 'active' : 'inactive'}
                                                    onClick={() => handleTempSubTypeToggle(subtype.Code)}
                                                >
                                                    <Badge
                                                        color={`#${subtype.BackColor}`}
                                                        style={{ marginRight: '6px' }}
                                                    />
                                                    {subtype.Name}
                                                </EventTypeTag>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {eventType === 'followup' && (
                                    <div className="filter-section">
                                        <div className="filter-label">Activity Labels</div>
                                        <div className="subtypes-tags">
                                            <EventTypeTag
                                                className={isAllLabelsSelected ? 'active' : 'inactive'}
                                                onClick={() => handleTempLabelToggle(null)}
                                            >
                                                All labels
                                            </EventTypeTag>
                                            {followUpLabels.map((labelItem) => (
                                                <EventTypeTag
                                                    key={labelItem.Code}
                                                    className={isLabelSelected(labelItem.Code) ? 'active' : 'inactive'}
                                                    onClick={() => handleTempLabelToggle(labelItem.Code)}
                                                >
                                                    <Badge color={labelItem.BackColor} style={{ marginRight: '6px' }} />
                                                    {labelItem.Label}
                                                </EventTypeTag>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="popover-actions">
                                <Button size="small" onClick={handleClearFilters}>
                                    Clear
                                </Button>
                                <Button size="small" type="primary" onClick={handleApplyFilters}>
                                    Search
                                </Button>
                            </div>
                        </>
                    )}
            </PopoverContent>
        );
    };

    const hasEvents = events && events.length > 0;

    // Debounced name filter state
    const [debouncedName, setDebouncedName] = useState(nameFilter);

    useEffect(() => {
        setDebouncedName(nameFilter);
    }, [nameFilter]);

    // Use lodash debounce for the filter
    const debouncedChange = useCallback(
        debounce((value) => {
            if (value !== nameFilter) {
                handleChangeNameFilter({ target: { value } });
            }
        }, 500),
        [nameFilter]
    );

    useEffect(() => {
        debouncedChange(debouncedName);
        return () => {
            debouncedChange.cancel();
        };
    }, [debouncedName, debouncedChange]);

    return (
        <StyledHeader>
            <div className="header-filters">
                <div className="header-filters-first-row">
                    <div className="header-summary">
                        {!loading ? (
                            <div className="header-summary-text">
                                <em className="counter">{`${events.length} EVENTS FOUND`}</em>
                            </div>
                        ) : (
                            <FlexContainer alignItems="center">
                                <div className="loading-container">
                                    <MiniLoading />
                                </div>
                                <p className="loading-title">Fetching Events...</p>
                            </FlexContainer>
                        )}
                    </div>
                    <div className="control">
                        <Input
                            allowClear
                            autoComplete="off"
                            placeholder="Filter by name"
                            prefix={<NwIcon icon={byPrefixAndName.fasl['search']} />}
                            value={debouncedName}
                            onChange={(e) => setDebouncedName(e.target.value)}
                        />
                    </div>
                    {hasEvents && (
                        <div className="control">
                            <ButtonGroup size="small" style={{ zIndex: '0' }}>
                                <NwButtonOption active={sort === 'time-up'} onClick={handleChangeSort('time-up')}>
                                    TIME &nbsp;
                                    <NwIcon icon={byPrefixAndName.fasl['arrow-up']} />
                                </NwButtonOption>
                                <NwButtonOption active={sort === 'time-down'} onClick={handleChangeSort('time-down')}>
                                    TIME &nbsp;
                                    <NwIcon icon={byPrefixAndName.fasl['arrow-down']} />
                                </NwButtonOption>
                                {!groupByProject && (
                                    <NwButtonOption active={sort === 'type'} onClick={handleChangeSort('type')}>
                                        TYPE
                                    </NwButtonOption>
                                )}
                            </ButtonGroup>
                        </div>
                    )}
                </div>
                <div className="header-filters-second-row">
                    <Popover
                        content={renderEventTypePopover('casting', 'Castings')}
                        trigger="click"
                        open={popoverOpen['casting']}
                        onOpenChange={(open) => handlePopoverChange('casting', open)}
                        placement="bottomLeft"
                    >
                        <EventTypeTag className={isEventTypeActive('casting') ? 'active' : 'inactive'}>
                            Castings
                            {isEventTypeActive('casting') && hasActiveFilters('casting') && (
                                <NwIcon
                                    icon={byPrefixAndName.fasl['filter']}
                                    style={{ marginLeft: '6px', fontSize: '0.9em' }}
                                />
                            )}
                        </EventTypeTag>
                    </Popover>
                    <Popover
                        content={renderEventTypePopover('job', 'Jobs')}
                        trigger="click"
                        open={popoverOpen['job']}
                        onOpenChange={(open) => handlePopoverChange('job', open)}
                        placement="bottomLeft"
                    >
                        <EventTypeTag className={isEventTypeActive('job') ? 'active' : 'inactive'}>
                            Jobs
                            {isEventTypeActive('job') && hasActiveFilters('job') && (
                                <NwIcon
                                    icon={byPrefixAndName.fasl['filter']}
                                    style={{ marginLeft: '6px', fontSize: '0.9em' }}
                                />
                            )}
                        </EventTypeTag>
                    </Popover>
                    <Popover
                        content={renderEventTypePopover('ext', 'Ext Jobs')}
                        trigger="click"
                        open={popoverOpen['ext']}
                        onOpenChange={(open) => handlePopoverChange('ext', open)}
                        placement="bottomLeft"
                    >
                        <EventTypeTag className={isEventTypeActive('ext') ? 'active' : 'inactive'}>
                            Ext Jobs
                            {isEventTypeActive('ext') && hasActiveFilters('ext') && (
                                <NwIcon
                                    icon={byPrefixAndName.fasl['filter']}
                                    style={{ marginLeft: '6px', fontSize: '0.9em' }}
                                />
                            )}
                        </EventTypeTag>
                    </Popover>
                    {(drawerType === 'model' || drawerType === 'global') && (
                        <>
                            <Popover
                                content={renderEventTypePopover('followup', 'Activities')}
                                trigger="click"
                                open={popoverOpen['followup']}
                                onOpenChange={(open) => handlePopoverChange('followup', open)}
                                placement="bottomLeft"
                            >
                                <EventTypeTag className={isEventTypeActive('followup') ? 'active' : 'inactive'}>
                                    Activities
                                    {isEventTypeActive('followup') && hasActiveFilters('followup') && (
                                        <NwIcon
                                            icon={byPrefixAndName.fasl['filter']}
                                            style={{ marginLeft: '6px', fontSize: '0.9em' }}
                                        />
                                    )}
                                </EventTypeTag>
                            </Popover>
                            <EventTypeTag
                                className={isEventTypeActive('travel') ? 'active' : 'inactive'}
                                onClick={() => handleToggleEventType('travel', !isEventTypeActive('travel'))}
                            >
                                Travels
                            </EventTypeTag>
                            <EventTypeTag
                                className={isEventTypeActive('accommodation') ? 'active' : 'inactive'}
                                onClick={() =>
                                    handleToggleEventType('accommodation', !isEventTypeActive('accommodation'))
                                }
                            >
                                Accommodations
                            </EventTypeTag>
                            <EventTypeTag
                                className={isEventTypeActive('roomLodging') ? 'active' : 'inactive'}
                                onClick={() => handleToggleEventType('roomLodging', !isEventTypeActive('roomLodging'))}
                            >
                                Room Lodging
                            </EventTypeTag>
                        </>
                    )}
                    <EventTypeTag
                        className={isEventTypeActive('extjob') ? 'active' : 'inactive'}
                        onClick={() => handleToggleEventType('extjob', !isEventTypeActive('extjob'))}
                    >
                        Moves
                    </EventTypeTag>
                    {drawerType === 'client' && (
                        <EventTypeTag
                            className={isEventTypeActive('meeting') ? 'active' : 'inactive'}
                            onClick={() => handleToggleEventType('meeting', !isEventTypeActive('meeting'))}
                        >
                            Meetings
                        </EventTypeTag>
                    )}
                </div>
                <div className="header-filters-third-row">
                    {isEventTypeActive('casting') && renderActiveFilters('casting')}
                    {isEventTypeActive('job') && renderActiveFilters('job')}
                    {isEventTypeActive('ext') && renderActiveFilters('ext')}
                    {isEventTypeActive('followup') && renderActiveFilters('followup')}
                </div>
            </div>
            <div className="header-actions">
                <p>
                    <Switch
                        size="small"
                        text="group by project"
                        checked={eventsGrouped}
                        onChange={handleGroupEvents}
                        disabled={!hasEvents}
                    />
                </p>
                <p>
                    <Switch
                        size="small"
                        text="Show fees/expenses"
                        disabled={!eventsGrouped || !hasEvents}
                        checked={showTransactions}
                        onChange={handleShowTransactions}
                    />
                </p>
            </div>
        </StyledHeader>
    );
};

export default EventsSearchHeaderDrawer;
